import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { FaPlus, FaEdit, FaTrash, FaEye, FaEuroSign, FaCalendar, FaBox, FaUpload, FaTimes, FaFilePdf, FaImage } from 'react-icons/fa';

export default function MyRentalListings() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const imageInputRef = useRef(null);
  const docInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    name_fr: '',
    name_en: '',
    description_fr: '',
    description_en: '',
    category_id: '',
    location: '',
    price_rental: '',
    stock_rental: 1,
    delivery_time: '24h',
    images: [],
    documents: [],
    technical_specs: {
      weight: '',
      dimensions: '',
      power: '',
      fuel: '',
      capacity: ''
    },
    included_accessories: [],
    rental_conditions: ''
  });
  
  const [newAccessory, setNewAccessory] = useState('');

  useEffect(() => {
    if (!user || (!user.roles?.includes('supplier') && !user.roles?.includes('admin'))) {
      navigate('/');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const [productsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/products`),
        axios.get(`${API}/categories`)
      ]);
      
      const myRentalProducts = productsRes.data.filter(p => 
        p.created_by_user_id === user.id && p.price_rental
      );
      setProducts(myRentalProducts);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingProduct(null);
    setFormData({
      name_fr: '',
      name_en: '',
      description_fr: '',
      description_en: '',
      category_id: '',
      location: '',
      price_rental: '',
      stock_rental: 1,
      delivery_time: '24h',
      images: [],
      documents: [],
      technical_specs: {
        weight: '',
        dimensions: '',
        power: '',
        fuel: '',
        capacity: ''
      },
      included_accessories: [],
      rental_conditions: ''
    });
    setShowModal(true);
  };

  const openEditModal = (product) => {
    setEditingProduct(product);
    setFormData({
      name_fr: product.name_fr,
      name_en: product.name_en,
      description_fr: product.description_fr,
      description_en: product.description_en,
      category_id: product.category_id,
      location: product.location || '',
      price_rental: product.price_rental,
      stock_rental: product.stock_rental,
      delivery_time: product.delivery_time || '24h',
      images: product.images || [],
      documents: product.documents || [],
      technical_specs: product.technical_specs || {
        weight: '',
        dimensions: '',
        power: '',
        fuel: '',
        capacity: ''
      },
      included_accessories: product.included_accessories || [],
      rental_conditions: product.rental_conditions || ''
    });
    setShowModal(true);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.images.length + files.length > 10) {
      toast.error('Maximum 10 images autorisées');
      return;
    }

    // Validate file types
    const validExtensions = ['jpg', 'jpeg', 'png', 'webp'];
    const invalidFiles = files.filter(file => {
      const ext = file.name.split('.').pop().toLowerCase();
      return !validExtensions.includes(ext);
    });

    if (invalidFiles.length > 0) {
      toast.error(`Fichiers non valides : ${invalidFiles.map(f => f.name).join(', ')}. Formats acceptés : JPG, PNG, WebP`);
      return;
    }

    // Validate file sizes
    const oversizedFiles = files.filter(file => file.size > 10 * 1024 * 1024);
    if (oversizedFiles.length > 0) {
      toast.error(`Fichiers trop volumineux : ${oversizedFiles.map(f => f.name).join(', ')}. Max 10 MB par image`);
      return;
    }

    setUploading(true);
    const formDataUpload = new FormData();
    files.forEach(file => formDataUpload.append('files', file));

    try {
      const response = await axios.post(`${API}/products/upload-images`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData({
        ...formData,
        images: [...formData.images, ...response.data.images]
      });
      toast.success(`${files.length} image(s) téléchargée(s)`);
    } catch (error) {
      console.error('Error uploading images:', error);
      const errorMsg = error.response?.data?.detail || 'Erreur lors du téléchargement des images';
      toast.error(errorMsg);
    } finally {
      setUploading(false);
    }
  };

  const handleDocumentUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    if (formData.documents.length + files.length > 5) {
      toast.error('Maximum 5 documents autorisés');
      return;
    }

    setUploading(true);
    const formDataUpload = new FormData();
    files.forEach(file => formDataUpload.append('files', file));

    try {
      const response = await axios.post(`${API}/products/upload-documents`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFormData({
        ...formData,
        documents: [...formData.documents, ...response.data.documents]
      });
      toast.success(`${files.length} document(s) téléchargé(s)`);
    } catch (error) {
      console.error('Error uploading documents:', error);
      toast.error('Erreur lors du téléchargement des documents');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index) => {
    setFormData({
      ...formData,
      images: formData.images.filter((_, i) => i !== index)
    });
  };

  const removeDocument = (index) => {
    setFormData({
      ...formData,
      documents: formData.documents.filter((_, i) => i !== index)
    });
  };

  const addAccessory = () => {
    if (!newAccessory.trim()) return;
    setFormData({
      ...formData,
      included_accessories: [...formData.included_accessories, newAccessory.trim()]
    });
    setNewAccessory('');
  };

  const removeAccessory = (index) => {
    setFormData({
      ...formData,
      included_accessories: formData.included_accessories.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const productData = {
        ...formData,
        price_rental: parseFloat(formData.price_rental),
        stock_rental: parseInt(formData.stock_rental),
        created_by_user_id: user.id,
        status: editingProduct ? editingProduct.status : 'pending'
      };

      if (editingProduct) {
        await axios.put(`${API}/products/${editingProduct.id}`, productData);
        toast.success('Annonce mise à jour');
      } else {
        await axios.post(`${API}/products`, productData);
        toast.success('Annonce créée ! Elle sera visible après approbation par l\'admin.');
      }

      setShowModal(false);
      fetchData();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette annonce ?')) {
      return;
    }

    try {
      await axios.delete(`${API}/products/${productId}`);
      toast.success('Annonce supprimée');
      fetchData();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const toggleAvailability = async (product) => {
    try {
      const newStock = product.stock_rental > 0 ? 0 : 1;
      await axios.put(`${API}/products/${product.id}`, {
        stock_rental: newStock
      });
      toast.success(newStock > 0 ? 'Produit disponible' : 'Produit indisponible');
      fetchData();
    } catch (error) {
      console.error('Error updating availability:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F5F5] flex items-center justify-center">
        <div className="text-xl text-[#0A2540]">{t('loading')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-[#0A2540] mb-2">
              Mes Annonces de Location
            </h1>
            <p className="text-gray-600">
              Gérez vos équipements disponibles à la location
            </p>
          </div>
          <Button
            onClick={openCreateModal}
            className="bg-[#00D4FF] hover:bg-[#00B4D8] w-full md:w-auto"
          >
            <FaPlus className="mr-2" /> Nouvelle Annonce
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Total Annonces</p>
                  <p className="text-3xl font-bold text-[#0A2540]">{products.length}</p>
                </div>
                <FaBox className="text-4xl text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Disponibles</p>
                  <p className="text-3xl font-bold text-[#635BFF]">
                    {products.filter(p => p.stock_rental > 0 && p.status === 'active').length}
                  </p>
                </div>
                <FaCalendar className="text-4xl text-[#635BFF]" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Revenu Potentiel/Jour</p>
                  <p className="text-3xl font-bold text-[#00D4FF]">
                    {products.filter(p => p.status === 'active').reduce((sum, p) => sum + (p.price_rental || 0), 0).toFixed(0)}€
                  </p>
                </div>
                <FaEuroSign className="text-4xl text-[#00D4FF]" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Products List */}
        {products.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <FaBox className="mx-auto text-6xl text-gray-300 mb-4" />
              <p className="text-gray-500 text-lg mb-4">
                Vous n'avez pas encore d'annonces de location
              </p>
              <Button onClick={openCreateModal} className="bg-[#00D4FF] hover:bg-[#00B4D8]">
                <FaPlus className="mr-2" /> Créer ma première annonce
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {products.map((product) => (
              <Card key={product.id} className="hover:shadow-lg transition-all">
                <div className="relative">
                  <img
                    src={product.images && product.images.length > 0 ? `${API}${product.images[0]}` : '/placeholder-equipment.jpg'}
                    alt={i18n.language === 'fr' ? product.name_fr : product.name_en}
                    className="w-full h-48 object-cover rounded-t-lg"
                  />
                  {product.status === 'pending' && (
                    <Badge className="absolute top-2 left-2 bg-yellow-500 text-white">
                      ⏳ En attente
                    </Badge>
                  )}
                  {product.status === 'rejected' && (
                    <Badge className="absolute top-2 left-2 bg-red-600 text-white">
                      ❌ Rejetée
                    </Badge>
                  )}
                  <Badge 
                    className={`absolute top-2 right-2 ${product.stock_rental > 0 ? 'bg-[#635BFF]' : 'bg-red-500'} text-white`}
                  >
                    {product.stock_rental > 0 ? 'Disponible' : 'Indisponible'}
                  </Badge>
                </div>
                
                <CardContent className="p-4">
                  <h3 className="font-bold text-lg mb-2 text-[#0A2540] line-clamp-2">
                    {i18n.language === 'fr' ? product.name_fr : product.name_en}
                  </h3>
                  
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                    {i18n.language === 'fr' ? product.description_fr : product.description_en}
                  </p>

                  {product.status === 'rejected' && product.admin_notes && (
                    <div className="mb-3 p-2 bg-red-50 border border-red-200 rounded text-xs">
                      <p className="text-red-800 font-semibold">Raison du rejet :</p>
                      <p className="text-red-700">{product.admin_notes}</p>
                    </div>
                  )}

                  {product.status === 'pending' && (
                    <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs">
                      <p className="text-yellow-800">⏳ En attente d'approbation par l'admin</p>
                    </div>
                  )}

                  <div className="flex justify-between items-center mb-4">
                    <div>
                      <div className="text-2xl font-bold text-[#00D4FF]">
                        {product.price_rental}€
                      </div>
                      <div className="text-sm text-gray-500">par jour</div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-600">Stock</div>
                      <div className="text-lg font-bold text-[#0A2540]">
                        {product.stock_rental}
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => navigate(`/rental/${product.id}`)}
                      className="flex-1"
                    >
                      <FaEye className="mr-1" /> Voir
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditModal(product)}
                      className="flex-1"
                    >
                      <FaEdit className="mr-1" /> Éditer
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleAvailability(product)}
                      className={product.stock_rental > 0 ? 'text-red-600' : 'text-[#635BFF]'}
                    >
                      {product.stock_rental > 0 ? '✕' : '✓'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Modal */}
        <Dialog open={showModal} onOpenChange={setShowModal}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl text-[#0A2540]">
                {editingProduct ? 'Modifier l\'annonce' : 'Nouvelle annonce de location'}
              </DialogTitle>
            </DialogHeader>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold text-[#0A2540] mb-4">📝 Informations de base</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Nom (Français) *</Label>
                    <Input
                      required
                      value={formData.name_fr}
                      onChange={(e) => setFormData({...formData, name_fr: e.target.value})}
                      placeholder="Ex: Pelle hydraulique Caterpillar 320D"
                    />
                  </div>
                  <div>
                    <Label>Nom (Anglais) *</Label>
                    <Input
                      required
                      value={formData.name_en}
                      onChange={(e) => setFormData({...formData, name_en: e.target.value})}
                      placeholder="Ex: Caterpillar 320D Hydraulic Excavator"
                    />
                  </div>
                </div>

                <div className="mt-4">
                  <Label>Description (Français) *</Label>
                  <Textarea
                    required
                    rows={3}
                    value={formData.description_fr}
                    onChange={(e) => setFormData({...formData, description_fr: e.target.value})}
                    placeholder="Décrivez votre équipement en détail..."
                  />
                </div>

                <div className="mt-4">
                  <Label>Description (Anglais) *</Label>
                  <Textarea
                    required
                    rows={3}
                    value={formData.description_en}
                    onChange={(e) => setFormData({...formData, description_en: e.target.value})}
                    placeholder="Describe your equipment in detail..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div>
                    <Label>Catégorie *</Label>
                    <Select 
                      value={formData.category_id} 
                      onValueChange={(value) => setFormData({...formData, category_id: value})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner..." />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map(cat => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {i18n.language === 'fr' ? cat.name_fr : cat.name_en}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Localisation</Label>
                    <Input
                      value={formData.location}
                      onChange={(e) => setFormData({...formData, location: e.target.value})}
                      placeholder="Ex: Paris 15ème"
                    />
                  </div>

                  <div>
                    <Label>Prix / jour (€) *</Label>
                    <Input
                      type="number"
                      required
                      min="0"
                      step="0.01"
                      value={formData.price_rental}
                      onChange={(e) => setFormData({...formData, price_rental: e.target.value})}
                      placeholder="350"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label>Stock disponible *</Label>
                    <Input
                      type="number"
                      required
                      min="0"
                      value={formData.stock_rental}
                      onChange={(e) => setFormData({...formData, stock_rental: e.target.value})}
                      placeholder="1"
                    />
                  </div>

                  <div>
                    <Label>Délai de livraison</Label>
                    <Input
                      value={formData.delivery_time}
                      onChange={(e) => setFormData({...formData, delivery_time: e.target.value})}
                      placeholder="24h"
                    />
                  </div>
                </div>
              </div>

              {/* Images */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold text-[#0A2540] mb-4">📸 Photos ({formData.images.length}/10)</h3>
                <input
                  type="file"
                  ref={imageInputRef}
                  onChange={handleImageUpload}
                  accept="image/jpeg,image/png,image/webp"
                  multiple
                  className="hidden"
                />
                <Button
                  type="button"
                  onClick={() => imageInputRef.current?.click()}
                  disabled={uploading || formData.images.length >= 10}
                  variant="outline"
                  className="w-full mb-4"
                >
                  <FaImage className="mr-2" />
                  {uploading ? 'Téléchargement...' : 'Ajouter des photos'}
                </Button>

                {formData.images.length > 0 && (
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                    {formData.images.map((img, idx) => (
                      <div key={idx} className="relative group">
                        <img
                          src={`${API}${img}`}
                          alt={`Photo ${idx + 1}`}
                          className="w-full h-24 object-cover rounded"
                        />
                        <button
                          type="button"
                          onClick={() => removeImage(idx)}
                          className="absolute top-1 right-1 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <FaTimes size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  Formats acceptés : JPEG, PNG, WebP | Max 10 MB par image
                </p>
              </div>

              {/* Documents */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold text-[#0A2540] mb-4">📄 Documents PDF ({formData.documents.length}/5)</h3>
                <input
                  type="file"
                  ref={docInputRef}
                  onChange={handleDocumentUpload}
                  accept="application/pdf"
                  multiple
                  className="hidden"
                />
                <Button
                  type="button"
                  onClick={() => docInputRef.current?.click()}
                  disabled={uploading || formData.documents.length >= 5}
                  variant="outline"
                  className="w-full mb-4"
                >
                  <FaFilePdf className="mr-2" />
                  {uploading ? 'Téléchargement...' : 'Ajouter manuel / fiche technique'}
                </Button>

                {formData.documents.length > 0 && (
                  <div className="space-y-2">
                    {formData.documents.map((doc, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 p-3 rounded">
                        <div className="flex items-center gap-2">
                          <FaFilePdf className="text-red-500" />
                          <div>
                            <p className="text-sm font-medium">{doc.filename}</p>
                            <p className="text-xs text-gray-500">{(doc.size / 1024).toFixed(0)} KB</p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeDocument(idx)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <FaTimes />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  Format PDF uniquement | Max 20 MB par document
                </p>
              </div>

              {/* Technical Specs */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold text-[#0A2540] mb-4">⚙️ Caractéristiques techniques</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label>Poids</Label>
                    <Input
                      value={formData.technical_specs.weight}
                      onChange={(e) => setFormData({
                        ...formData,
                        technical_specs: {...formData.technical_specs, weight: e.target.value}
                      })}
                      placeholder="Ex: 20 tonnes"
                    />
                  </div>
                  <div>
                    <Label>Dimensions (L x l x H)</Label>
                    <Input
                      value={formData.technical_specs.dimensions}
                      onChange={(e) => setFormData({
                        ...formData,
                        technical_specs: {...formData.technical_specs, dimensions: e.target.value}
                      })}
                      placeholder="Ex: 8m x 3m x 3.5m"
                    />
                  </div>
                  <div>
                    <Label>Puissance</Label>
                    <Input
                      value={formData.technical_specs.power}
                      onChange={(e) => setFormData({
                        ...formData,
                        technical_specs: {...formData.technical_specs, power: e.target.value}
                      })}
                      placeholder="Ex: 150 CV"
                    />
                  </div>
                  <div>
                    <Label>Carburant</Label>
                    <Input
                      value={formData.technical_specs.fuel}
                      onChange={(e) => setFormData({
                        ...formData,
                        technical_specs: {...formData.technical_specs, fuel: e.target.value}
                      })}
                      placeholder="Ex: Diesel"
                    />
                  </div>
                  <div>
                    <Label>Capacité</Label>
                    <Input
                      value={formData.technical_specs.capacity}
                      onChange={(e) => setFormData({
                        ...formData,
                        technical_specs: {...formData.technical_specs, capacity: e.target.value}
                      })}
                      placeholder="Ex: 1.2 m³"
                    />
                  </div>
                </div>
              </div>

              {/* Accessories */}
              <div className="border-b pb-6">
                <h3 className="text-lg font-semibold text-[#0A2540] mb-4">🔧 Accessoires inclus</h3>
                <div className="flex gap-2 mb-4">
                  <Input
                    value={newAccessory}
                    onChange={(e) => setNewAccessory(e.target.value)}
                    placeholder="Ex: Godet standard 1m³"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addAccessory())}
                  />
                  <Button type="button" onClick={addAccessory} variant="outline">
                    <FaPlus />
                  </Button>
                </div>
                {formData.included_accessories.length > 0 && (
                  <div className="space-y-2">
                    {formData.included_accessories.map((acc, idx) => (
                      <div key={idx} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                        <span className="text-sm">✓ {acc}</span>
                        <button
                          type="button"
                          onClick={() => removeAccessory(idx)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <FaTimes size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Rental Conditions */}
              <div>
                <h3 className="text-lg font-semibold text-[#0A2540] mb-4">📋 Conditions de location</h3>
                <Textarea
                  rows={4}
                  value={formData.rental_conditions}
                  onChange={(e) => setFormData({...formData, rental_conditions: e.target.value})}
                  placeholder="Ex: Caution de 5000€ demandée. Assurance tous risques incluse. Livraison possible dans un rayon de 50km."
                />
              </div>

              <div className="bg-blue-50 p-4 rounded-lg">
                <p className="text-sm text-blue-800">
                  <strong>💡 Conseil :</strong> Plus votre annonce est complète (photos, documents, caractéristiques), plus vous avez de chances de louer !
                </p>
              </div>

              <div className="flex gap-3 justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowModal(false)}
                >
                  Annuler
                </Button>
                <Button type="submit" className="bg-[#00D4FF] hover:bg-[#00B4D8]">
                  {editingProduct ? 'Mettre à jour' : 'Créer l\'annonce'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
